import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PartyBalance } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { IconArrowDownCircle, IconArrowUpCircle } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchPayables, fetchReceivables, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { amountSign, formatCount, formatMoney, formatPosDate } from '@/lib/format';

type BalanceKind = 'receivables' | 'payables';

const COPY: Record<BalanceKind, { title: string; description: string; party: string; outstanding: string }> = {
  receivables: {
    title: 'Receivables',
    description:
      'Customer balances still open from the credit activity in the selected range — charged less settled.',
    party: 'Customer',
    outstanding: 'Owed to you',
  },
  payables: {
    title: 'Payables',
    description:
      'Supplier balances still open from the purchase activity in the selected range — charged less settled.',
    party: 'Supplier',
    outstanding: 'You owe',
  },
};

function BalancesPage({ kind }: { kind: BalanceKind }) {
  const { currency, filters } = usePortal();
  const copy = COPY[kind];
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);

  const balanceQuery = useQuery({
    queryKey: kind === 'receivables' ? portalKeys.receivables(range) : portalKeys.payables(range),
    queryFn: ({ signal }) =>
      kind === 'receivables' ? fetchReceivables(range, signal) : fetchPayables(range, signal),
  });

  const reportCurrency = balanceQuery.data?.currency || currency;

  const columns = useMemo<Array<Column<PartyBalance>>>(
    () => [
      {
        key: 'name',
        header: copy.party,
        cell: (row) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-900">{row.name || '—'}</p>
            {row.phone ? <p className="mt-0.5 text-xs text-slate-400">{row.phone}</p> : null}
          </div>
        ),
      },
      {
        key: 'charged',
        header: 'Charged',
        align: 'right',
        visibility: 'hidden sm:table-cell',
        cellClassName: 'tabular-nums',
        cell: (row) => formatMoney(row.charged, reportCurrency),
      },
      {
        key: 'settled',
        header: 'Settled',
        align: 'right',
        visibility: 'hidden sm:table-cell',
        cellClassName: 'tabular-nums',
        cell: (row) => formatMoney(row.settled, reportCurrency),
      },
      {
        key: 'balance',
        header: 'Balance',
        align: 'right',
        cell: (row) => (
          <span
            className={`tabular-nums ${
              amountSign(row.balance) > 0 ? 'font-semibold text-slate-900' : 'text-slate-500'
            }`}
          >
            {formatMoney(row.balance, reportCurrency)}
          </span>
        ),
      },
      {
        key: 'lastActivity',
        header: 'Last activity',
        align: 'right',
        visibility: 'hidden md:table-cell',
        cellClassName: 'whitespace-nowrap text-slate-500',
        cell: (row) => (row.lastActivity === null ? '—' : formatPosDate(row.lastActivity)),
      },
    ],
    [copy.party, reportCurrency],
  );

  const icon = kind === 'receivables' ? <IconArrowDownCircle className="h-5 w-5" /> : <IconArrowUpCircle className="h-5 w-5" />;

  const header = (
    <PageHeader
      title={copy.title}
      description={copy.description}
      actions={
        <ExportMenu
          report={kind}
          range={range}
          disabled={balanceQuery.isPending || balanceQuery.isError}
        />
      }
    />
  );

  // One query feeds the tiles and the table, so a failure is reported once.
  if (balanceQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={balanceQuery.error}
            onRetry={() => void balanceQuery.refetch()}
            retrying={balanceQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={balanceQuery}
          skeleton={
            <KpiGrid>
              <KpiTileSkeleton />
              <KpiTileSkeleton />
            </KpiGrid>
          }
        >
          {(report) => (
            <KpiGrid>
              <KpiTile
                label={copy.outstanding}
                value={formatMoney(report.totalOutstanding, report.currency || currency)}
                tone={amountSign(report.totalOutstanding) > 0 ? 'negative' : 'positive'}
              />
              <KpiTile
                label={`${copy.party}s with a balance`}
                value={formatCount(report.rows.filter((row) => amountSign(row.balance) !== 0).length)}
                hint={`${formatCount(report.rows.length)} in total`}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard headingId="balances-table" title={`${copy.party}s`} count={balanceQuery.data?.rows.length}>
          <AsyncSection
            state={balanceQuery}
            skeleton={<TableSkeleton rows={6} columns={5} />}
            isEmpty={(report) => report.rows.length === 0}
            emptyIcon={icon}
            emptyTitle="Nothing outstanding"
            emptyDescription={
              kind === 'receivables'
                ? 'No customer currently owes anything.'
                : 'No supplier balance is currently outstanding.'
            }
          >
            {(report) => (
              <DataTable
                caption={`${copy.title}: outstanding balance per ${copy.party.toLowerCase()}`}
                columns={columns}
                rows={report.rows}
                rowKey={(row) => String(row.sourceId)}
                totals={{
                  label: 'Total outstanding',
                  cells: {
                    balance: formatMoney(report.totalOutstanding, report.currency || currency),
                  },
                }}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}

export function ReceivablesPage() {
  return <BalancesPage kind="receivables" />;
}

export function PayablesPage() {
  return <BalancesPage kind="payables" />;
}
